import { Privilege } from "services/rbac/types";

import {
  accountsPrivileges,
  loginsPrivileges,
  networkPoliciesPrivileges,
  orgRolesPrivileges,
  serviceAccountsPrivileges,
  ssoPrivileges,
} from "pages/configure/privileges";
import {
  ownershipPrivileges,
  rolesPrivileges,
  usersPrivileges,
} from "pages/govern/privileges";

import { useAccessManager } from "components/App/accessManager";

import { useCurrentAccount } from "../Account/useCurrentAccount";

const getGovernRoute = ({
  allowRoles,
  allowUsers,
  allowOwnership,
}: {
  allowRoles: boolean;
  allowUsers: boolean;
  allowOwnership: boolean;
}) => {
  if (allowUsers) {
    return "/users";
  }
  if (allowRoles) {
    return "/roles";
  }
  if (allowOwnership) {
    return "/ownership";
  }

  return "/";
};

const getConfigureRoute = ({
  allowAccounts,
  allowLogins,
  allowNetworkPolicies,
  allowServiceAccounts,
  allowOrgRoles,
  allowSSO,
}: {
  allowAccounts: boolean;
  allowLogins: boolean;
  allowNetworkPolicies: boolean;
  allowServiceAccounts: boolean;
  allowOrgRoles: boolean;
  allowSSO: boolean;
}) => {
  if (allowAccounts) {
    return "/accounts";
  }
  if (allowLogins) {
    return "/logins";
  }
  if (allowNetworkPolicies) {
    return "/network-policies";
  }
  if (allowOrgRoles) {
    return "/organization-roles";
  }
  if (allowServiceAccounts) {
    return "/service-accounts";
  }
  if (allowSSO) {
    return "/sso";
  }
  return "/";
};

export const useShowMenuItems = () => {
  const { isAllowedTo } = useAccessManager();
  const { getAccount } = useCurrentAccount();

  const accountName = getAccount().accountName;

  const privilegeMatch = (privilege: Privilege) =>
    isAllowedTo(
      privilege.objectType,
      privilege.objectName,
      privilege.privilegeType
    );

  const governAccess = {
    allowUsers: usersPrivileges(accountName).some(privilegeMatch),
    allowRoles: rolesPrivileges(accountName).some(privilegeMatch),
    allowOwnership: ownershipPrivileges(accountName).some(privilegeMatch),
  };

  const configureAccess = {
    allowAccounts: accountsPrivileges.some(privilegeMatch),
    allowLogins: loginsPrivileges.some(privilegeMatch),
    allowNetworkPolicies: networkPoliciesPrivileges.some(privilegeMatch),
    allowServiceAccounts: serviceAccountsPrivileges.some(privilegeMatch),
    allowOrgRoles: orgRolesPrivileges.some(privilegeMatch),
    allowSSO: ssoPrivileges.some(privilegeMatch),
  };

  return {
    configure: Object.values(configureAccess).some(section => section),
    configureRoute: getConfigureRoute(configureAccess),
    govern: Object.values(governAccess).some(section => section),
    governRoute: getGovernRoute(governAccess),
  };
};
